/**
 * Toast store. One toast at a time (a new one replaces the current, as in the design); it can
 * carry an undo token (server `undoToken`) and/or one action button. Callable from anywhere —
 * components, mutation callbacks, the query client — and rendered by <Toaster/>.
 */
import { useSyncExternalStore } from 'react';

/** Design v2: a toast stays 6 s (capture results 8 s). */
export const TOAST_DURATION_MS = 6_000;

export interface ToastAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface ToastOptions {
  /** Server undo token; shows "Undo" which calls POST /api/v1/undo/:token. */
  readonly undoToken?: string | null;
  readonly action?: ToastAction;
  /** Extra buttons after «Отменить» (design: «Дополнить сейчас»). */
  readonly actions?: readonly ToastAction[];
  readonly tone?: 'default' | 'error';
  readonly durationMs?: number;
}

export interface Toast extends ToastOptions {
  readonly id: number;
  readonly text: string;
}

interface ToastState {
  readonly current: Toast | null;
  /** Number of mounted bulk toolbars; toasts lift above them (design: 84px instead of 24px). */
  readonly liftCount: number;
}

type Listener = () => void;

let state: ToastState = { current: null, liftCount: 0 };
let nextId = 1;
const listeners = new Set<Listener>();

function setState(next: ToastState): void {
  state = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function showToast(text: string, options: ToastOptions = {}): number {
  const id = nextId++;
  setState({ ...state, current: { id, text, ...options } });
  return id;
}

export function showErrorToast(text: string): number {
  return showToast(text, { tone: 'error' });
}

export function dismissToast(id?: number): void {
  if (!state.current) return;
  if (id !== undefined && state.current.id !== id) return;
  setState({ ...state, current: null });
}

/** Registers a bottom toolbar while mounted; returns the unregister function. */
export function liftToasts(): () => void {
  setState({ ...state, liftCount: state.liftCount + 1 });
  return () => setState({ ...state, liftCount: Math.max(0, state.liftCount - 1) });
}

export function getToastState(): ToastState {
  return state;
}

export function useToastState(): ToastState {
  return useSyncExternalStore(subscribe, getToastState, getToastState);
}

/** Test helper: forget everything. */
export function resetToasts(): void {
  setState({ current: null, liftCount: 0 });
}

/** Convenience for mutation results shaped like `{ undoToken?: string }`. */
function toastWithUndo(
  text: string,
  result: { readonly undoToken?: string | null } | null | undefined,
): number {
  return showToast(text, { undoToken: result?.undoToken ?? null });
}

export const toast = {
  show: showToast,
  error: showErrorToast,
  dismiss: dismissToast,
  withUndo: toastWithUndo,
};
